import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, ArrowRight } from "lucide-react";

function CostCalculator({ calc }) {
  const [users, setUsers] = useState(calc.defaultUsers || 1);
  const clampUsers = (n) => Math.max(1, Math.min(calc.maxUsers || 50, n));
  const monthly = calc.servicingFee + users * calc.perUser;
  const fmt = (n) => "$" + n.toLocaleString("en-US");
  const estimate = {
    service: "CRM & Sales Systems",
    users,
    monthly,
    message:
      `CRM & Sales Systems — estimate for ${users} user${users === 1 ? "" : "s"}: ` +
      `${fmt(monthly)}/mo (${fmt(calc.servicingFee)} servicing + ${users} × ${fmt(calc.perUser)} Close.io). ` +
      `Excludes one-time ${fmt(calc.setup)} setup.`,
  };
  return (
    <div className="mt-5 rounded-[10px] border border-kv-steel/20 bg-kv-bg2 p-4" data-testid="crm-cost-calculator">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel mb-3">{calc.label}</div>
      <div className="flex items-center justify-between gap-4">
        <span className="font-body text-sm text-kv-grey">CRM users</span>
        <div className="inline-flex items-center gap-3">
          <button type="button" aria-label="Fewer users" data-testid="calc-users-minus" onClick={() => setUsers((u) => clampUsers(u - 1))} className="inline-flex h-8 w-8 items-center justify-center border border-kv-steel/30 text-kv-off hover:border-kv-electric/60 transition-colors">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-8 text-center font-body text-base font-medium text-kv-off tabular-nums" data-testid="calc-users-count">{users}</span>
          <button type="button" aria-label="More users" data-testid="calc-users-plus" onClick={() => setUsers((u) => clampUsers(u + 1))} className="inline-flex h-8 w-8 items-center justify-center border border-kv-steel/30 text-kv-off hover:border-kv-electric/60 transition-colors">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-kv-steel/15 space-y-1.5">
        <div className="flex items-center justify-between font-body text-[13px] text-kv-grey">
          <span>Monthly servicing fee</span><span className="tabular-nums">{fmt(calc.servicingFee)}</span>
        </div>
        <div className="flex items-center justify-between font-body text-[13px] text-kv-grey">
          <span>Close.io — {users} × {fmt(calc.perUser)}</span><span className="tabular-nums">{fmt(users * calc.perUser)}</span>
        </div>
        <div className="flex items-center justify-between pt-2 mt-1 border-t border-kv-steel/15">
          <span className="font-display text-sm font-medium text-kv-off">Estimated monthly</span>
          <span className="font-display text-lg font-medium text-kv-off tabular-nums" data-testid="calc-monthly-total">{fmt(monthly)}<span className="font-body text-xs text-kv-steel">/mo</span></span>
        </div>
      </div>
      <p className="mt-3 font-body text-[11px] text-kv-steel leading-relaxed">Excludes one-time {fmt(calc.setup)} setup. Estimate only — final pricing depends on scope and usage.</p>
      <Link to="/contact" state={{ estimate }} data-testid="calc-book-cta" className="group mt-4 inline-flex w-full items-center justify-center gap-2 px-5 py-3 text-sm font-medium tracking-wide bg-kv-off text-kv-bg hover:bg-kv-off/90 transition-colors duration-300">
        Book a strategy call
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  );
}

function Pack({ pack }) {
  return (
    <div className="border-t border-kv-steel/15 pt-6 first:border-t-0 first:pt-0">
      <div className="mb-4">
        <h4 className="font-display text-lg md:text-xl font-medium tracking-tight text-kv-off">{pack.name}</h4>
        <p className="mt-1.5 font-body text-sm font-medium text-kv-off leading-snug">{pack.price}</p>
      </div>

      {pack.includes && (
        <ul className="space-y-2">
          {pack.includes.map((it) => (
            <li key={it} className="flex items-start gap-2.5 font-body text-sm text-kv-grey leading-relaxed">
              <span className="mt-[7px] h-1 w-1 rounded-full bg-kv-electric shrink-0" /> {it}
            </li>
          ))}
        </ul>
      )}

      {pack.usage && (
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel mb-2">{pack.usage.label}</div>
          <ul className="space-y-1">
            {pack.usage.lines.map((l) => (
              <li key={l} className="font-body text-sm text-kv-grey leading-relaxed">{l}</li>
            ))}
          </ul>
        </div>
      )}

      {pack.plans && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pack.plans.map((pl) => (
            <div key={pl.name} className="border border-kv-steel/15 rounded-[10px] p-4">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <span className="font-display text-sm font-medium text-kv-off">{pl.name}</span>
                <span className="font-body text-[13px] font-medium text-kv-off whitespace-nowrap">{pl.price}</span>
              </div>
              <ul className="space-y-1">
                {pl.points.map((p) => (
                  <li key={p} className="font-body text-[13px] text-kv-grey leading-snug">{p}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {pack.calculator && <CostCalculator calc={pack.calculator} />}

      {pack.note && <p className="mt-4 font-body text-xs text-kv-steel leading-relaxed">{pack.note}</p>}
      {pack.disclosure && (
        <p className="mt-3 font-body text-xs text-kv-steel leading-relaxed border-l-2 border-kv-steel/40 pl-3">{pack.disclosure}</p>
      )}
    </div>
  );
}

export default function PricingModal({ group, onClose }) {
  useEffect(() => {
    if (!group) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const lenis = window.__lenis;
    if (lenis && lenis.stop) lenis.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      if (lenis && lenis.start) lenis.start();
    };
  }, [group, onClose]);

  if (!group) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-label={group.title} data-testid="pricing-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in-0" onClick={onClose} data-testid="pricing-modal-overlay" />
      <div
        data-lenis-prevent
        className="relative z-10 w-full max-w-xl max-h-[85vh] overflow-y-auto overscroll-contain bg-kv-bg border border-kv-steel/20 rounded-[14px] p-7 md:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.55)] animate-in fade-in-0 zoom-in-95"
      >
        <button onClick={onClose} aria-label="Close pricing" data-testid="pricing-modal-close" className="absolute right-4 top-4 text-kv-steel hover:text-kv-off transition-colors">
          <X className="h-5 w-5" />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-kv-steel mb-1">Pricing</div>
        <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-kv-off pr-8">{group.title}</h3>

        <div className="space-y-6 mt-6">
          {group.packs.map((p) => <Pack key={p.name} pack={p} />)}
        </div>

        <p className="mt-6 pt-4 border-t border-kv-steel/15 font-body text-[11px] text-kv-steel leading-relaxed">
          Prices shown in USD. Third-party subscriptions, usage, telephony, messaging, and premium model fees are billed separately unless expressly listed. Final pricing depends on scope, integrations, and usage volume.
        </p>
      </div>
    </div>
  );
}
